import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { IUserAuthContract } from "@features/student/auth/model";
import { createEvent, createStore, sample } from "effector";
import { checkUpsellPurchased, UpsellStatus } from "@features/helpers";
import { routePathNameChanged } from "@features/app/model";
import { interval, reset } from "patronum";
import { COMPLETE_BUNDLE_UPSELL_IDS, IUpsellContract } from "../model";
import { CREATIVE_FAMILIES, CREATIVE_UPSELLS } from "@features/upsell/creative";
import { ISubscriptionContract } from "@features/subscription/model";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IUpsellUIContract } from ".";
import { IClaudeWorkBundleContract } from "./claude-work-bundle";

const TARGET_IDS = new Set(["30k_prompts_complete_pack", "30k_prompts_complete_pack_sale"]);
const BUNDLE_CHECK_ID = "bundle";
// The drama / film skills ship this pack as a free gift.
const CREATIVE_SKILL_IDS = CREATIVE_FAMILIES.flatMap((family) => [
    CREATIVE_UPSELLS[family].skill,
    CREATIVE_UPSELLS[family].skillSale
]);

// Always check BOTH complete-pack variants (full + sale) plus the bundles and
// the drama / film skills, regardless of what the current sequence/metadata
// lists. A user may have bought the sale variant in an older funnel that the
// current Claude sequence no longer includes (only the full 30k is appended
// post-funnel), so filtering by $upsells would miss their purchase and lock
// the pack. Every Complete Bundle grants this pack too.
const buildCompletePackCheckIds = (): string[] => [
    ...TARGET_IDS,
    BUNDLE_CHECK_ID,
    ...COMPLETE_BUNDLE_UPSELL_IDS,
    ...CREATIVE_SKILL_IDS
];

const buyButtonClicked = createEvent();
const buyButtonDisabled = createEvent();
const buyButtonReset = createEvent();
const modalOpened = createEvent();
const modalClosed = createEvent();
const pollingStarted = createEvent();
const pollingStopped = createEvent();

const $isModalOpen = createStore(false)
    .on(modalOpened, () => true)
    .on(modalClosed, () => false);
const $upsellIds = createStore<string[]>([]);
const $completeAiPackStatus = createStore<UpsellStatus | null>(null);
const checkRequested = createEvent();
const checkFinished = createEvent();
const $isCheckRequested = createStore(false).on(checkRequested, () => true);
const $isCheckPending = createStore(false)
    .on(checkRequested, () => true)
    .on(checkFinished, () => false);
const $isBuyButtonDisabled = createStore(true)
    .on(buyButtonClicked, () => true)
    .on(buyButtonDisabled, () => true)
    .on(buyButtonReset, () => false);

const { tick } = interval({
    timeout: 10000,
    start: pollingStarted,
    stop: pollingStopped,
    leading: false,
    trailing: false
});

const contract = new ModelContract(
    { $isModalOpen, $completeAiPackStatus, $upsellIds, $isBuyButtonDisabled },
    { buyButtonClicked, modalOpened, modalClosed },
    {},
    {}
);

export type ICompleteAiPackContract = typeof contract;

export class CompleteAiPackModule extends ModuleDescription<ICompleteAiPackContract> {
    constructor(
        public authModule: IUserAuthContract,
        public routerModule: IRouterContract,
        public paymentModule: IPaymentContract,
        public upsellModule: IUpsellContract,
        public upsellUiModule: IUpsellUIContract,
        public subscriptionsModule: ISubscriptionContract,
        public routesModule: IAppRouteUnitsContract,
        public claudeWorkBundleModule: IClaudeWorkBundleContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // The check needs no catalog: truegate plans are resolved on the
        // server now, and the ids checked are fixed anyway.
        sample({
            source: {
                upsells: this.upsellModule.stores.$upsells,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            fn: () => buildCompletePackCheckIds(),
            target: $upsellIds
        });

        sample({
            clock: modalClosed,
            fn: () => "/upsell/30k_prompts_complete_pack_sale",
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: this.upsellUiModule.events.upsellSaleModalOpened,
            source: { currentUpsellId: this.upsellModule.stores.$currentUpsellId },
            filter: (s) => s.currentUpsellId === "30k_prompts_complete_pack",
            target: modalOpened
        });

        sample({
            clock: buyButtonClicked,
            target: this.upsellModule.events.upsellBought
        });

        sample({
            source: {
                upsellIds: $upsellIds,
                upsellPath: this.routesModule.stores.upsell.$path,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isLibraryRoute: this.routesModule.stores.library.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isChallengesRoute: this.routesModule.stores.challenges.$isActive,
                promptLibraryQuery: this.routesModule.stores.promptsLibrary.$query,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckRequested: $isCheckRequested,
                isCheckPending: $isCheckPending
            },
            filter: (s) =>
                s.upsellIds.length > 0 &&
                (s.isHomeRoute ||
                    s.isProfileRoute ||
                    // Desktop right rail shows the packs on these routes too.
                    s.isLibraryRoute ||
                    s.isAiToolsRoute ||
                    s.isChallengesRoute ||
                    s.promptLibraryQuery.pack === "ultimate_ai" ||
                    (s.upsellPath !== null &&
                        (s.upsellPath.includes("30k_prompts_complete_pack") || s.upsellPath.includes("bundle")))) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckRequested &&
                !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.done,
            source: {
                upsellIds: $upsellIds,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                awaitingVerdict: this.upsellModule.stores.$upsellIdsAwaitingVerdict
            },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem),
            fn: (s, { result }) => {
                const statuses = s.upsellIds.map((upsellId) => checkUpsellPurchased(result, upsellId));

                if (statuses.includes("Success")) return "Success";
                if (statuses.includes("Processing")) return "Processing";
                // A just-bought pack in 3ds_verify reads as "Fail" until its bank
                // dialog is done; it is in progress — keep polling until the
                // webhook confirms instead of showing it as unpurchased.
                if (s.upsellIds.some((upsellId) => s.awaitingVerdict.includes(upsellId))) return "Processing";

                return "Fail";
            },
            target: $completeAiPackStatus
        });

        sample({
            source: {
                completeAiPackStatus: $completeAiPackStatus,
                claudeWorkBundleStatus: this.claudeWorkBundleModule.stores.$claudeWorkBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path,
                firstLoginFlag: this.upsellModule.stores.$firstLoginFlag
            },
            filter: (s) => {
                const hasPackAccess =
                    s.completeAiPackStatus === "Success" ||
                    s.completeAiPackStatus === "Processing" ||
                    s.claudeWorkBundleStatus === "Success" ||
                    s.claudeWorkBundleStatus === "Processing";

                return (
                    hasPackAccess &&
                    s.isUpsellRoute &&
                    s.upsellPath !== null &&
                    s.upsellPath.includes("30k_prompts_complete_pack") &&
                    !s.firstLoginFlag
                );
            },
            target: [this.upsellUiModule.events.packAccessRedirectTriggered, buyButtonDisabled]
        });

        sample({
            source: {
                completeAiPackStatus: $completeAiPackStatus,
                claudeWorkBundleStatus: this.claudeWorkBundleModule.stores.$claudeWorkBundleStatus,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                upsellPath: this.routesModule.stores.upsell.$path
            },
            filter: (s) => {
                const hasPackAccess =
                    s.completeAiPackStatus === "Success" ||
                    s.completeAiPackStatus === "Processing" ||
                    s.claudeWorkBundleStatus === "Success" ||
                    s.claudeWorkBundleStatus === "Processing";

                return (
                    !hasPackAccess &&
                    s.isUpsellRoute &&
                    s.upsellPath !== null &&
                    s.upsellPath.includes("30k_prompts_complete_pack")
                );
            },
            target: buyButtonReset
        });

        sample({
            clock: $completeAiPackStatus,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s, p) => !s.isUpsellRoute && p === "Processing",
            target: pollingStarted
        });

        sample({
            clock: $completeAiPackStatus,
            filter: (status) => status === "Success" || status === "Fail",
            target: pollingStopped
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.finally,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: checkFinished
        });

        sample({
            clock: this.upsellModule.effects.checkUpsellPurchasedFx.fail,
            source: { upsellIds: $upsellIds },
            filter: (s, { params }) =>
                s.upsellIds.length > 0 &&
                s.upsellIds.length === params.length &&
                s.upsellIds.every((id) => params.includes(id)),
            fn: () => undefined,
            target: [pollingStarted, buyButtonReset]
        });

        sample({
            clock: tick,
            source: { upsellIds: $upsellIds, isCheckPending: $isCheckPending },
            filter: (s) => s.upsellIds.length > 0 && !s.isCheckPending,
            fn: (s) => s.upsellIds,
            target: [this.upsellModule.effects.checkUpsellPurchasedFx, checkRequested]
        });

        reset({
            clock: routePathNameChanged,
            target: [$isModalOpen, $completeAiPackStatus, $isCheckRequested]
        });
    }
}
